import { useState } from "react";
import { useTranslation } from "react-i18next";
import { teamMembers } from "@/mocks/about";

export default function TeamSection() {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section className="section-padding bg-cream">
      <div className="container-main">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <span className="tag-label mb-4 inline-block">{t("about.team.label")}</span>
          <h2 className="mb-4">{t("about.team.title")}</h2>
          <p className="text-medium-gray text-base">
            建築師、設計師、工程專家與美學顧問，組成跨領域整合團隊，為你的理想宅把關每一個環節。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {teamMembers.map((member) => {
            const isExpanded = expandedId === member.id;
            return (
              <div
                key={member.id}
                className="bg-white rounded-xl overflow-hidden border border-warm-gray/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl group"
              >
                {/* Avatar */}
                <div className="relative h-56 md:h-64 overflow-hidden bg-warm-gray/50">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-graphite/50 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                    <div>
                      <h3 className="text-white text-lg md:text-xl font-bold mb-0.5">
                        {member.name}
                        {member.nameEn && (
                          <span className="text-cream/80 text-sm font-sans font-normal ml-1.5">
                            {member.nameEn}
                          </span>
                        )}
                      </h3>
                      <p className="text-champagne text-sm font-sans font-semibold">
                        {member.role}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-5 md:p-6">
                  {/* Specialty tags */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {member.specialty.map((s) => (
                      <span
                        key={s}
                        className="px-2.5 py-1 rounded-full bg-champagne/10 text-champagne text-xs font-medium"
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  {/* Short desc */}
                  <p className="text-medium-gray text-[1rem] leading-relaxed mb-0">
                    {member.desc}
                  </p>

                  {/* Expandable details */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      isExpanded ? "max-h-48 opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"
                    }`}
                  >
                    <div className="pt-4 border-t border-warm-gray/20 space-y-2.5">
                      <div className="flex items-start gap-2.5">
                        <span className="w-4 h-4 flex items-center justify-center text-champagne shrink-0 mt-0.5">
                          <i className="ri-graduation-cap-line text-sm" />
                        </span>
                        <div>
                          <p className="text-xs text-medium-gray mb-0.5">學歷 / 資格</p>
                          <p className="text-sm text-graphite font-medium">{member.education}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="w-4 h-4 flex items-center justify-center text-champagne shrink-0 mt-0.5">
                          <i className="ri-briefcase-4-line text-sm" />
                        </span>
                        <div>
                          <p className="text-xs text-medium-gray mb-0.5">資歷</p>
                          <p className="text-sm text-graphite font-medium">{member.experience}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="w-4 h-4 flex items-center justify-center text-champagne shrink-0 mt-0.5">
                          <i className="ri-building-2-line text-sm" />
                        </span>
                        <div>
                          <p className="text-xs text-medium-gray mb-0.5">代表專案</p>
                          <p className="text-sm text-graphite font-medium">{member.projects}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expand toggle */}
                  <button
                    onClick={() => toggleExpand(member.id)}
                    className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-cream text-champagne text-sm font-medium transition-all duration-200 hover:bg-champagne/10 cursor-pointer"
                  >
                    <span>{isExpanded ? "收起專業背景" : "查看專業背景"}</span>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}